import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { brandConfig } from '../../../config/brands';
import { useQueryClient } from '@tanstack/react-query';
import { usePromptAttributes } from '../../generation/hooks/usePromptAttributes';
import { PersonGenerationStep } from './steps/PersonGenerationStep';
import { AttributeEditStep } from './steps/AttributeEditStep';
import { CharacterSheetStep } from './steps/CharacterSheetStep';
import { FaceCropStep } from './steps/FaceCropStep';
import { CharacterTraitsStep } from './steps/CharacterTraitsStep';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { isCharacterActor } from '../../../features/actors/actorAttributes';
import type { GeneratePersonRequest, GeneratePersonResponse, GenerateCharacterSheetRequest, GenerateCharacterSheetResponse, SuggestNameFn, SuggestNameResponse } from '../../../features/admin/actors/api';
import styles from './ActorGenerationWizard.module.css';
import '../../../styles/buttons.css';
import '../../../styles/generation-common.css';

// Step 5 (特徴設定) はキャラクター (ethnicity='character') のときだけ現れる
type WizardStep = 1 | 2 | 3 | 4 | 5;

type GeneratedImage = {
  base64: string;
  mimeType: string;
  /** 生成画像の素材 ID。アップロード画像・旧バックエンドの応答では null */
  materialId?: string | null;
};

type PersonImageMode = 'generate' | 'upload';

// 2026-09-09: gpt-image-2-auto → gpt-image-2.5-flare-auto (backend actor_admin_service.ALLOWED_ACTOR_MODELS と同期)
type ActorModel = 'gpt-image-2.5-flare-auto' | 'gemini-3-pro-image-preview';

type WizardData = {
  // 生成モデル（人物画像 = Step1）
  model: ActorModel;
  // 生成モデル（キャラクターシート = Step3）。Step1 とは独立して選択する
  characterSheetModel: ActorModel;

  // Step 1: 人物生成
  personImageMode: PersonImageMode;
  personImageBase64: string | null;
  personMimeType: string | null;
  /** 表示中の人物画像の素材 ID (AI生成の結果)。シート生成・名前提案はこれを参照で渡す。
   *  アップロード画像は null で、その場合は base64 を送ってバックエンド側で一時素材化する */
  personMaterialId: string | null;
  personImageHistory: GeneratedImage[];  // 生成履歴
  personImageIndex: number;              // 現在表示中のインデックス
  prompt: string;
  gender: 'male' | 'female' | 'none';
  ageMin: number | null;
  ageMax: number | null;
  ethnicity: string;
  bodyType: string;
  physicalFeatures: string;

  // Step 2: 名前設定
  displayName: string;       // 表示名（和名）
  displayNameEn: string;     // 英語表示名
  internalName: string;
  tags: string[];

  // Step 3: キャラクターシート
  characterSheetBase64: string | null;
  characterSheetMimeType: string | null;
  /** 表示中のキャラクターシートの素材 ID (旧バックエンドの応答では null) */
  characterSheetMaterialId: string | null;
  characterSheetHistory: GeneratedImage[];  // キャラクターシート生成履歴
  characterSheetIndex: number;              // 現在表示中のインデックス

  // Step 4: 顔写真
  faceImageBase64: string | null;
  faceCropArea: { x: number; y: number; width: number; height: number } | null;

  // Step 5 (キャラクターのみ): 特徴・設定テキスト。AIプロンプトへそのまま追加される
  characterTraits: string;
};

const initialWizardData: WizardData = {
  model: 'gemini-3-pro-image-preview',
  characterSheetModel: 'gemini-3-pro-image-preview',
  personImageMode: 'generate',
  personImageBase64: null,
  personMimeType: null,
  personMaterialId: null,
  personImageHistory: [],
  personImageIndex: -1,
  prompt: '',
  gender: 'none',
  ageMin: null,
  ageMax: null,
  ethnicity: 'jp',
  bodyType: 'none',
  physicalFeatures: 'none',
  displayName: '',
  displayNameEn: '',
  internalName: '',
  tags: [],
  characterSheetBase64: null,
  characterSheetMimeType: null,
  characterSheetMaterialId: null,
  characterSheetHistory: [],
  characterSheetIndex: -1,
  faceImageBase64: null,
  faceCropArea: null,
  characterTraits: '',
};

// API function types
type GeneratePersonFn = (request: GeneratePersonRequest) => Promise<GeneratePersonResponse>;
type GenerateCharacterSheetFn = (request: GenerateCharacterSheetRequest) => Promise<GenerateCharacterSheetResponse>;
// SuggestNameFn は features/admin/actors/api.ts の正本を使う (options 経由で AbortSignal を通すため)

type InitialWizardData = {
  personImageMode?: 'generate' | 'upload';
  personImageBase64?: string;
  personMimeType?: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (data: WizardData) => void | Promise<void>;
  // 生成 API（非同期ジョブ版を呼び出し元が注入。同期経路は廃止済み）
  generatePersonApi: GeneratePersonFn;
  generateCharacterSheetApi: GenerateCharacterSheetFn;
  suggestNameApi: SuggestNameFn;
  // 内部名の空き確認 (グローバル一意)。未指定なら名前設定ステップでのチェックを行わない
  checkNameApi?: (name: string) => Promise<{ available: boolean }>;
  // Optional initial data (e.g., pre-set image from profile page)
  initialData?: InitialWizardData;
};

const STEP_LABELS = ['人物生成', '名前設定', 'キャラクターシート', '顔写真'] as const;
// キャラクター (ethnicity='character') のときだけ最後に現れるステップ
const CHARACTER_TRAITS_STEP_LABEL = '特徴設定';

// 画像拡大モーダル
function ImageLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  // ライトボックス表示中はピンチズームを有効化
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    // ピンチズームを許可するviewport設定に変更
    viewport?.setAttribute('content', 'width=device-width, initial-scale=1.0');

    return () => {
      // 元のviewport設定に戻す（index.htmlの設定）
      viewport?.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    };
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return createPortal(
    <div className={styles.lightboxOverlay} onClick={handleOverlayClick}>
      <button type="button" className={styles.lightboxClose} onClick={handleCloseClick}>×</button>
      <img src={src} alt={alt} className={styles.lightboxImage} onClick={(e) => e.stopPropagation()} />
    </div>,
    document.body
  );
}

export function ActorGenerationWizard({ isOpen, onClose, onComplete, generatePersonApi, generateCharacterSheetApi, suggestNameApi, checkNameApi, initialData }: Props) {
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [wizardData, setWizardData] = useState<WizardData>(initialWizardData);
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);
  const [slideAnimation, setSlideAnimation] = useState<'slideInFromLeft' | 'slideInFromRight' | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // 画像生成完了時にジョブリスト（通知）を更新
  const handleGenerateSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['generation', 'jobs'] });
  }, [queryClient]);

  // ウィザードを開いた時に状態をリセット（初期データがあれば適用）
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);

      // 初期データがあれば適用
      if (initialData) {
        const newWizardData = {
          ...initialWizardData,
          personImageMode: initialData.personImageMode || 'upload',
          personImageBase64: initialData.personImageBase64 || null,
          personMimeType: initialData.personMimeType || null,
          personMaterialId: null,
          personImageHistory: initialData.personImageBase64
            ? [{ base64: initialData.personImageBase64, mimeType: initialData.personMimeType || 'image/jpeg', materialId: null }]
            : [],
          personImageIndex: initialData.personImageBase64 ? 0 : -1,
        };
        setWizardData(newWizardData);
      } else {
        setWizardData(initialWizardData);
      }

      setLightboxImage(null);
      setIsCompleting(false);

      // モーダル表示中は背景スクロールを無効化
      document.body.style.overflow = 'hidden';
    } else {
      // モーダルを閉じる時は背景スクロールを復活
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialData]);

  // 属性ツリーを取得
  const attributeQuery = usePromptAttributes();

  // personノードから体格と身体的特徴を抽出
  const { buildOptions, appearanceOptions } = useMemo(() => {
    const personNode = attributeQuery.data?.items?.find(
      (node) => node.key === 'person'
    );

    const buildChild = personNode?.children?.find((child) => child.key === 'build');
    const appearanceChild = personNode?.children?.find(
      (child) => child.key === 'appearance'
    );

    return {
      buildOptions: buildChild?.children || [],
      appearanceOptions: appearanceChild?.children || [],
    };
  }, [attributeQuery.data]);

  // ウィザードデータを更新
  const updateWizardData = useCallback((updates: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...updates }));
  }, []);

  // ---- 名前提案の先読み ----------------------------------------------------
  // 名前候補は人物画像そのものではなくプロンプトと条件でほぼ決まるので、
  // 画像生成 (10〜30秒) と並行して投げておき、Step2 到達時には出来ている状態にする。
  const namePrefetchRef = useRef<{
    key: string;
    promise: Promise<SuggestNameResponse | null>;
  } | null>(null);

  // 条件が変わっていなければ結果を使い回すためのキー
  const buildNamePrefetchKey = useCallback((data: WizardData) => JSON.stringify([
    data.prompt,
    data.gender,
    data.ageMin,
    data.ageMax,
    data.ethnicity,
    data.bodyType,
    data.physicalFeatures,
  ]), []);

  const currentNamePrefetchKey = buildNamePrefetchKey(wizardData);

  const prefetchNameSuggestion = useCallback(() => {
    const key = buildNamePrefetchKey(wizardData);
    // 同じ条件で走らせ済みなら再取得しない (人物画像は何度も生成し直すため)
    if (namePrefetchRef.current?.key === key) return;

    const bodyTypeLabel = wizardData.bodyType && wizardData.bodyType !== 'none'
      ? buildOptions.find((o) => o.key === wizardData.bodyType)?.label
      : undefined;
    const physicalFeaturesLabel = wizardData.physicalFeatures && wizardData.physicalFeatures !== 'none'
      ? appearanceOptions.find((o) => o.key === wizardData.physicalFeatures)?.label
      : undefined;

    const promise = suggestNameApi({
      // 画像は渡さない (画像生成の完了を待たないため)
      ethnicity: wizardData.ethnicity,
      generation_prompt: wizardData.prompt || undefined,
      attributes: {
        gender: wizardData.gender !== 'none' ? wizardData.gender : undefined,
        age_min: wizardData.ageMin ?? undefined,
        age_max: wizardData.ageMax ?? undefined,
        body_type: bodyTypeLabel,
        physical_features: physicalFeaturesLabel,
      },
    }).catch((err) => {
      // 先読みの失敗は致命的ではない (Step2 で画像つきの通常経路にフォールバックする)
      console.warn('Failed to prefetch name suggestion:', err);
      return null;
    });

    namePrefetchRef.current = { key, promise };
  }, [wizardData, buildNamePrefetchKey, buildOptions, appearanceOptions, suggestNameApi]);

  // 先読み結果を 1 度だけ取り出す (条件が変わっていたら無効)
  const takePrefetchedNameSuggestion = useCallback((key: string) => {
    const entry = namePrefetchRef.current;
    if (!entry || entry.key !== key) return null;
    namePrefetchRef.current = null;
    return entry.promise;
  }, []);

  // キャラクター (ethnicity='character') は Step5 (特徴設定) まで。人間俳優は従来どおり
  // Step4 (顔写真) が最終ステップ。ethnicity は Step1 で決まるため以降は変化しない。
  const isCharacterWizard = isCharacterActor({ ethnicity: wizardData.ethnicity });
  const maxStep = (isCharacterWizard ? 5 : 4) as WizardStep;
  const stepLabels: string[] = isCharacterWizard
    ? [...STEP_LABELS, CHARACTER_TRAITS_STEP_LABEL]
    : [...STEP_LABELS];

  // 次のステップへ
  const goToNextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, maxStep) as WizardStep);
    // フォームカラムのスクロール位置をリセット
    formColumnRef.current?.scrollTo(0, 0);
  }, [maxStep]);

  // 「次へ」クリック。名前設定ステップ(2)では内部名のグローバル重複を事前チェックする
  const handleNextClick = useCallback(async () => {
    if (currentStep === 2 && checkNameApi && wizardData.internalName) {
      setIsCheckingName(true);
      try {
        const { available } = await checkNameApi(wizardData.internalName);
        if (!available) {
          alert(`内部名「${wizardData.internalName}」はすでに使われています。別の名前を入力してください。`);
          return;
        }
      } catch (err) {
        // チェック失敗時は通過させ、最終送信時のサーバ側チェックに委ねる
        console.error('Failed to check actor name availability:', err);
      } finally {
        setIsCheckingName(false);
      }
    }
    goToNextStep();
  }, [currentStep, checkNameApi, wizardData.internalName, goToNextStep]);

  // 前のステップへ
  const goToPrevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1) as WizardStep);
    // フォームカラムのスクロール位置をリセット
    formColumnRef.current?.scrollTo(0, 0);
  }, []);

  // Step1で画像生成成功時にフォームを最下段までスクロール＆通知更新
  const handlePersonGenerateSuccess = useCallback(() => {
    // フォームを最下段までスクロール
    if (formColumnRef.current) {
      formColumnRef.current.scrollTo({
        top: formColumnRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    // ジョブリスト（通知）を更新
    handleGenerateSuccess();
  }, [handleGenerateSuccess]);

  // 完了
  const handleComplete = useCallback(async () => {
    setIsCompleting(true);
    try {
      await onComplete(wizardData);
    } finally {
      setIsCompleting(false);
    }
  }, [wizardData, onComplete]);

  // 閉じるボタンを押した時
  const handleCloseClick = useCallback(() => {
    // 常に確認ダイアログを表示
    setShowCloseConfirm(true);
  }, []);

  // 確認ダイアログで「中断する」を選択
  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    setCurrentStep(1);
    setWizardData(initialWizardData);
    onClose();
  }, [onClose]);

  // 確認ダイアログで「続ける」を選択
  const handleCancelClose = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  // 画像履歴ナビゲーション
  const canNavigateHistory = currentStep === 1 && wizardData.personImageHistory.length > 1;
  const canGoPrevImage = canNavigateHistory && wizardData.personImageIndex > 0;
  const canGoNextImage = canNavigateHistory && wizardData.personImageIndex < wizardData.personImageHistory.length - 1;

  const goToPrevImage = useCallback(() => {
    if (!canGoPrevImage) return;
    const newIndex = wizardData.personImageIndex - 1;
    const image = wizardData.personImageHistory[newIndex];
    setSlideAnimation('slideInFromLeft');
    setWizardData((prev) => ({
      ...prev,
      personImageBase64: image.base64,
      personMimeType: image.mimeType,
      personMaterialId: image.materialId ?? null,
      personImageIndex: newIndex,
    }));
    // アニメーション終了後にリセット
    setTimeout(() => setSlideAnimation(null), 250);
  }, [canGoPrevImage, wizardData.personImageIndex, wizardData.personImageHistory]);

  const goToNextImage = useCallback(() => {
    if (!canGoNextImage) return;
    const newIndex = wizardData.personImageIndex + 1;
    const image = wizardData.personImageHistory[newIndex];
    setSlideAnimation('slideInFromRight');
    setWizardData((prev) => ({
      ...prev,
      personImageBase64: image.base64,
      personMimeType: image.mimeType,
      personMaterialId: image.materialId ?? null,
      personImageIndex: newIndex,
    }));
    // アニメーション終了後にリセット
    setTimeout(() => setSlideAnimation(null), 250);
  }, [canGoNextImage, wizardData.personImageIndex, wizardData.personImageHistory]);

  // キャラクターシート履歴ナビゲーション
  const canNavigateCharacterSheetHistory = currentStep === 3 && wizardData.characterSheetHistory.length > 1;
  const canGoPrevCharacterSheet = canNavigateCharacterSheetHistory && wizardData.characterSheetIndex > 0;
  const canGoNextCharacterSheet = canNavigateCharacterSheetHistory && wizardData.characterSheetIndex < wizardData.characterSheetHistory.length - 1;

  const goToPrevCharacterSheet = useCallback(() => {
    if (!canGoPrevCharacterSheet) return;
    const newIndex = wizardData.characterSheetIndex - 1;
    const image = wizardData.characterSheetHistory[newIndex];
    setSlideAnimation('slideInFromLeft');
    setWizardData((prev) => ({
      ...prev,
      characterSheetBase64: image.base64,
      characterSheetMimeType: image.mimeType,
      characterSheetMaterialId: image.materialId ?? null,
      characterSheetIndex: newIndex,
    }));
    // アニメーション終了後にリセット
    setTimeout(() => setSlideAnimation(null), 250);
  }, [canGoPrevCharacterSheet, wizardData.characterSheetIndex, wizardData.characterSheetHistory]);

  const goToNextCharacterSheet = useCallback(() => {
    if (!canGoNextCharacterSheet) return;
    const newIndex = wizardData.characterSheetIndex + 1;
    const image = wizardData.characterSheetHistory[newIndex];
    setSlideAnimation('slideInFromRight');
    setWizardData((prev) => ({
      ...prev,
      characterSheetBase64: image.base64,
      characterSheetMimeType: image.mimeType,
      characterSheetMaterialId: image.materialId ?? null,
      characterSheetIndex: newIndex,
    }));
    // アニメーション終了後にリセット
    setTimeout(() => setSlideAnimation(null), 250);
  }, [canGoNextCharacterSheet, wizardData.characterSheetIndex, wizardData.characterSheetHistory]);

  // 次へボタンの有効状態
  const canGoNext = useMemo(() => {
    switch (currentStep) {
      case 1: {
        // キャラクター (ethnicity='character') は性別が未指定でもよく、
        // 年齢範囲も持たない (P1-1)。
        const isCharacter = wizardData.ethnicity === 'character';
        if (wizardData.personImageMode === 'generate') {
          // AI生成モード: 画像生成済み & (キャラクターでなければ)性別選択済み
          return !!wizardData.personImageBase64 && (isCharacter || wizardData.gender !== 'none');
        } else {
          // アップロードモード: 画像アップロード済み & 人種が入力済み。
          // 性別・年齢範囲は人間俳優のときのみ必須。
          return !!wizardData.personImageBase64 &&
            (isCharacter || wizardData.gender !== 'none') &&
            (isCharacter || (wizardData.ageMin !== null && wizardData.ageMax !== null)) &&
            !!wizardData.ethnicity;
        }
      }
      case 2:
        return !!wizardData.displayName && !!wizardData.internalName;
      case 3:
        return !!wizardData.characterSheetBase64;
      case 4:
        return !!wizardData.faceImageBase64;
      case 5:
        // 特徴設定は任意入力 (空でも登録できる)
        return true;
      default:
        return false;
    }
  }, [currentStep, wizardData]);

  // 現在のステップに応じたプレビュー画像を取得
  const previewImage = useMemo(() => {
    if (currentStep === 1 || currentStep === 2) {
      if (wizardData.personImageBase64) {
        return {
          src: `data:${wizardData.personMimeType};base64,${wizardData.personImageBase64}`,
          alt: '生成された人物',
        };
      }
    } else if (currentStep === 3) {
      if (wizardData.characterSheetBase64) {
        return {
          src: `data:${wizardData.characterSheetMimeType};base64,${wizardData.characterSheetBase64}`,
          alt: 'キャラクターシート',
        };
      }
      // キャラクターシートがない場合は人物画像を表示
      if (wizardData.personImageBase64) {
        return {
          src: `data:${wizardData.personMimeType};base64,${wizardData.personImageBase64}`,
          alt: '生成された人物',
        };
      }
    } else if (currentStep === 4 || currentStep === 5) {
      if (wizardData.characterSheetBase64) {
        return {
          src: `data:${wizardData.characterSheetMimeType};base64,${wizardData.characterSheetBase64}`,
          alt: 'キャラクターシート',
        };
      }
    }
    return null;
  }, [currentStep, wizardData]);

  // フォームカラムのref（スクロール位置リセット用）
  const formColumnRef = useRef<HTMLDivElement>(null);

  // スワイプ用の状態
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const SWIPE_THRESHOLD = 50;

  // スワイプ開始
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  // スワイプ終了
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    // 水平方向のスワイプのみを検出（垂直方向の動きが小さい場合）
    if (Math.abs(deltaX) > SWIPE_THRESHOLD && deltaY < 80) {
      // Step 1: 人物画像の履歴
      if (currentStep === 1 && canNavigateHistory) {
        if (deltaX > 0 && canGoPrevImage) {
          goToPrevImage();
        } else if (deltaX < 0 && canGoNextImage) {
          goToNextImage();
        }
      }
      // Step 3: キャラクターシートの履歴
      else if (currentStep === 3 && canNavigateCharacterSheetHistory) {
        if (deltaX > 0 && canGoPrevCharacterSheet) {
          goToPrevCharacterSheet();
        } else if (deltaX < 0 && canGoNextCharacterSheet) {
          goToNextCharacterSheet();
        }
      }
    }
    touchStartRef.current = null;
  }, [currentStep, canNavigateHistory, canGoPrevImage, canGoNextImage, goToPrevImage, goToNextImage,
      canNavigateCharacterSheetHistory, canGoPrevCharacterSheet, canGoNextCharacterSheet, goToPrevCharacterSheet, goToNextCharacterSheet]);

  // オーバーレイクリックの処理（ドラッグ操作で誤って閉じないように）
  const handleOverlayMouseDown = useCallback((e: React.MouseEvent) => {
    // オーバーレイ自体でマウスダウンした場合のみフラグを立てる
    if (e.target === e.currentTarget) {
      setMouseDownOnOverlay(true);
    }
  }, []);

  const handleOverlayMouseUp = useCallback((e: React.MouseEvent) => {
    // オーバーレイでマウスダウン＆マウスアップした場合のみ閉じる
    if (mouseDownOnOverlay && e.target === e.currentTarget) {
      handleCloseClick();
    }
    setMouseDownOnOverlay(false);
  }, [mouseDownOnOverlay, handleCloseClick]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
    >
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        {/* ヘッダー: ステッパー + 閉じるボタン */}
        <div className={styles.header}>
          <div className={styles.stepper}>
            {stepLabels.map((label, index) => {
              const stepNum = (index + 1) as WizardStep;
              const isActive = currentStep === stepNum;
              const isCompleted = currentStep > stepNum;

              return (
                <div
                  key={label}
                  className={`${styles.stepItem} ${isActive ? styles.stepActive : ''} ${isCompleted ? styles.stepCompleted : ''}`}
                >
                  <div className={styles.stepCircle}>
                    {isCompleted ? <Check size={14} strokeWidth={2.5} /> : stepNum}
                  </div>
                  <span className={styles.stepLabel}>{label}</span>
                  {index < stepLabels.length - 1 && (
                    <div className={styles.stepConnector} />
                  )}
                </div>
              );
            })}
          </div>
          <button type="button" className={styles.closeButton} onClick={handleCloseClick}>
            ×
          </button>
        </div>

        {/* 2カラムレイアウト */}
        <div className={styles.mainContent}>
          {/* 左カラム: フォーム */}
          <div className={styles.formColumn} ref={formColumnRef}>
            {currentStep === 1 && (
              <PersonGenerationStep
                data={wizardData}
                onUpdate={updateWizardData}
                buildOptions={buildOptions}
                appearanceOptions={appearanceOptions}
                generatePersonApi={generatePersonApi}
                onGenerateSuccess={handlePersonGenerateSuccess}
                onPrefetchNames={prefetchNameSuggestion}
              />
            )}
            {currentStep === 2 && (
              <AttributeEditStep
                data={wizardData}
                onUpdate={updateWizardData}
                suggestNameApi={suggestNameApi}
                prefetchKey={currentNamePrefetchKey}
                takePrefetchedNames={takePrefetchedNameSuggestion}
              />
            )}
            {currentStep === 3 && (
              <CharacterSheetStep
                data={wizardData}
                onUpdate={updateWizardData}
                generateCharacterSheetApi={generateCharacterSheetApi}
                onGenerateSuccess={handleGenerateSuccess}
              />
            )}
            {currentStep === 4 && (
              <FaceCropStep
                data={wizardData}
                onUpdate={updateWizardData}
              />
            )}
            {currentStep === 5 && (
              <CharacterTraitsStep
                data={wizardData}
                onUpdate={updateWizardData}
              />
            )}
          </div>

          {/* 右カラム: プレビュー（顔写真ステップでは非表示） */}
          {currentStep !== 4 && (
          <div className={styles.previewColumn}>
            {previewImage ? (
              <div
                className={styles.previewWrapper}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                {/* 左矢印（人物画像用） */}
                {canNavigateHistory && (
                  <button
                    type="button"
                    className={`${styles.navArrow} ${styles.navArrowLeft}`}
                    onClick={goToPrevImage}
                    disabled={!canGoPrevImage}
                  >
                    ‹
                  </button>
                )}
                {/* 左矢印（キャラクターシート用） */}
                {canNavigateCharacterSheetHistory && (
                  <button
                    type="button"
                    className={`${styles.navArrow} ${styles.navArrowLeft}`}
                    onClick={goToPrevCharacterSheet}
                    disabled={!canGoPrevCharacterSheet}
                  >
                    ‹
                  </button>
                )}

                <div
                  className={`${styles.previewImageContainer} ${slideAnimation ? styles[slideAnimation] : ''}`}
                  onClick={() => setLightboxImage(previewImage)}
                >
                  <img src={previewImage.src} alt={previewImage.alt} />
                </div>

                {/* 右矢印（人物画像用） */}
                {canNavigateHistory && (
                  <button
                    type="button"
                    className={`${styles.navArrow} ${styles.navArrowRight}`}
                    onClick={goToNextImage}
                    disabled={!canGoNextImage}
                  >
                    ›
                  </button>
                )}
                {/* 右矢印（キャラクターシート用） */}
                {canNavigateCharacterSheetHistory && (
                  <button
                    type="button"
                    className={`${styles.navArrow} ${styles.navArrowRight}`}
                    onClick={goToNextCharacterSheet}
                    disabled={!canGoNextCharacterSheet}
                  >
                    ›
                  </button>
                )}

                {/* ページインジケーター（人物画像用） */}
                {canNavigateHistory && (
                  <div className={styles.pageIndicator}>
                    {wizardData.personImageIndex + 1} / {wizardData.personImageHistory.length}
                  </div>
                )}
                {/* ページインジケーター（キャラクターシート用） */}
                {canNavigateCharacterSheetHistory && (
                  <div className={styles.pageIndicator}>
                    {wizardData.characterSheetIndex + 1} / {wizardData.characterSheetHistory.length}
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.previewPlaceholder}>
                画像を生成してください
              </div>
            )}
          </div>
          )}
        </div>

        {/* フッター（モーダル全体幅） */}
        <div className={styles.footer}>
          <button
            type="button"
            className={`btn-modal btn-modal-secondary ${currentStep === 1 ? styles.btnSecondaryLeft : ''}`}
            onClick={currentStep === 1 ? handleCloseClick : goToPrevStep}
          >
            {currentStep === 1 ? 'キャンセル' : '戻る'}
          </button>
          {currentStep < maxStep ? (
            <button
              type="button"
              className={`btn-modal ${isCheckingName ? 'ai-loading-active' : ''}`}
              onClick={handleNextClick}
              disabled={!canGoNext || isCheckingName}
            >
              {isCheckingName ? '確認中...' : '次へ'}
            </button>
          ) : (
            <button
              type="button"
              className={`btn-modal ${isCompleting ? 'ai-loading-active' : ''}`}
              onClick={handleComplete}
              disabled={!canGoNext || isCompleting}
            >
              {isCompleting ? '登録中...' : '完了'}
            </button>
          )}
        </div>
      </div>

      {/* 画像拡大モーダル */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {/* 閉じる確認ダイアログ */}
      <ConfirmDialog
        isOpen={showCloseConfirm}
        title="作成を中断しますか？"
        message={`${brandConfig.labels.aiActor}の作成を中断すると、同じ人物の登録はできなくなります。`}
        confirmLabel="中断する"
        cancelLabel="続ける"
        variant="danger"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </div>,
    document.body
  );
}

export type { WizardData, InitialWizardData, ActorModel };
